import React, { useState, useCallback } from 'react';
import DomainBadge from '../../../shared/components/DomainBadge';
import { renderMarkdown } from '../../../shared/utils/markdownLite';

/**
 * Syntax Fix mode: show a config block with errors, user identifies and corrects them.
 */
export default function SyntaxFix({ questions, onComplete, onUpdateStats }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [corrections, setCorrections] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  const [startTime, setStartTime] = useState(Date.now());

  const currentQ = questions[currentIndex];

  const handleCorrection = (lineIdx, value) => {
    setCorrections(prev => ({ ...prev, [lineIdx]: value }));
  };

  const handleSubmit = useCallback(() => {
    if (!currentQ) return;
    setSubmitted(true);

    const errorLines = currentQ.error_lines || [];
    const configBlock = currentQ.config_block || [];
    let correctFixes = 0;

    // Check each error line
    for (const error of errorLines) {
      const lineIdx = configBlock.indexOf(error.original);
      if (lineIdx === -1) continue;

      const userFix = (corrections[lineIdx] || '').trim().toLowerCase();
      const expected = error.corrected.trim().toLowerCase();

      if (userFix === expected || expected.includes(userFix)) {
        correctFixes++;
      }
    }

    const percentage = errorLines.length > 0 ? Math.round((correctFixes / errorLines.length) * 100) : 0;
    const timeTaken = (Date.now() - startTime) / 1000;

    setSessionResults(prev => [...prev, {
      questionId: currentQ.id,
      percentage,
      correctFixes,
      totalErrors: errorLines.length,
      timeTaken,
    }]);

    onUpdateStats?.(currentQ.id, percentage === 100, timeTaken);
  }, [currentQ, corrections, startTime, onUpdateStats]);

  const handleNext = useCallback(() => {
    setSubmitted(false);
    setCorrections({});
    setStartTime(Date.now());
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete?.(sessionResults);
    }
  }, [currentIndex, questions.length, sessionResults, onComplete]);

  if (!currentQ) return null;

  const configBlock = currentQ.config_block || [];
  const errorLines = currentQ.error_lines || [];
  const errorOriginals = new Set(errorLines.map(e => e.original));

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress */}
      <div className="flex justify-between text-sm mb-2" style={{ color: '#00cc33' }}>
        <span>Question {currentIndex + 1} / {questions.length}</span>
        <span>{sessionResults.filter(r => r.percentage === 100).length} perfect fixes</span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-1 mb-6">
        <div className="h-1 rounded-full transition-all" style={{ width: `${(currentIndex / questions.length) * 100}%`, backgroundColor: '#00ff41' }} />
      </div>

      {/* Prompt */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <DomainBadge domainId={currentQ.domain} size="xs" />
          <span className="text-xs text-gray-500">{currentQ.category}</span>
        </div>
        <div className="text-white">{renderMarkdown(currentQ.prompt)}</div>
        <p className="text-yellow-400 text-sm mt-2">
          Find and fix {errorLines.length} error{errorLines.length !== 1 ? 's' : ''} in the configuration below:
        </p>
      </div>

      {/* Config block with editable error lines */}
      <div className="bg-black rounded-lg border border-gray-700 overflow-hidden mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 border-b border-gray-700">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-xs text-gray-500 ml-2 font-mono">{currentQ.context_mode || 'Router#'}</span>
        </div>
        <div className="p-4 space-y-1">
          {configBlock.map((line, idx) => {
            const isError = errorOriginals.has(line);
            const errorInfo = errorLines.find(e => e.original === line);

            if (!isError) {
              // Correct line — display only
              return (
                <div key={idx} className="font-mono text-sm text-green-400 px-2 py-1">
                  {line}
                </div>
              );
            }

            // Error line — show original + editable correction
            return (
              <div key={idx} className="border border-red-800/50 rounded bg-red-900/10 px-2 py-1">
                <div className="font-mono text-sm text-red-400 line-through opacity-60">
                  {line}
                </div>
                {!submitted ? (
                  <input
                    type="text"
                    value={corrections[idx] || ''}
                    onChange={(e) => handleCorrection(idx, e.target.value)}
                    placeholder="Type the corrected command..."
                    className="w-full mt-1 px-2 py-1 bg-black border border-green-800 rounded text-green-400 font-mono text-sm outline-none focus:border-green-500"
                  />
                ) : (
                  <div className="mt-1">
                    {/* Show user's correction */}
                    {corrections[idx] && (
                      <div className={`font-mono text-sm px-2 py-1 rounded ${
                        corrections[idx].trim().toLowerCase() === errorInfo.corrected.trim().toLowerCase()
                          ? 'text-green-400 bg-green-900/20'
                          : 'text-red-400 bg-red-900/20'
                      }`}>
                        {corrections[idx] || '(no correction)'}
                      </div>
                    )}
                    {/* Show correct answer */}
                    <div className="font-mono text-sm text-blue-400 px-2 py-1 mt-1">
                      Correct: {errorInfo.corrected}
                    </div>
                    <div className="text-xs text-gray-400 px-2 py-1">
                      {errorInfo.explanation}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Submit / Next */}
      <div className="flex justify-end">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 font-mono text-sm"
          >
            Check Fixes
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 font-mono text-sm"
          >
            {currentIndex + 1 < questions.length ? 'Next Question →' : 'Finish'}
          </button>
        )}
      </div>
    </div>
  );
}
